// Página "Faturamento": tarefa do Plano de ação (Ciclo 1 › Semana 1) e aba de
// Financeiro › DRE & Resultado. Total, por setor e por cliente, mês a mês, no período
// escolhido. Os números são os da Receita bruta da DRE: a soma dos setores é igual à soma
// dos clientes e igual ao total (o servidor confere).
// Rota: GET /financeiro/faturamento-12m (dre-engine; `de`/`ate`, até 36 meses). Contas puras
// em dadosSetores.ts. O nome do arquivo fica (registro do Plano de ação em paginas/index.ts).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, Segmented, Select, Space, Table, Tag, Tooltip as TipAntd, Typography } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePerms } from '../../../hooks/usePerms';
import { metaSetor } from '../../../utils/setoresDre';
import {
  buscarFaturamento12m, ErroDre, type Agrupar, type FatCliente, type Faturamento12m as Resposta,
  type PropsPagina, type TipoGrupoCliente,
} from './apiDre';
import {
  clientesDoSetor, clientesTop, composicaoPorSetor, linhasDoGrafico, MAX_MESES_FATURAMENTO, pctTexto, porSetorCanonico, reais, reaisCurto,
  rotuloMes, rotuloMesCurto, serieDaEmpresa, serieDoCliente, serieDoSetor, setoresDaResposta, tendenciaTexto,
} from './dadosSetores';
import {
  AcessoNaoConferido, Carregando, COR_TENDENCIA, EtiquetaSetor, FiltroDePeriodo, FiltroDeSetor, FiltroDeTendencia, IrPara,
  LINK_AREAS, SemAcesso, TooltipEmpilhado, TRACO_TENDENCIA, usePeriodo,
} from './comumDre';

const { Text } = Typography;
const PERM = 'financeiro.dre.ver';
const COR_SO_TOTAL = '#8c8c8c';

/** Texto de um contrato da lista do cliente (o servidor pode mandar só o número ou um objeto). */
function rotuloContrato(c: any): string {
  if (c == null) return '';
  if (typeof c === 'string' || typeof c === 'number') return String(c);
  return String(c.numero || c.contrato_numero || c.codigo || c.id || '');
}

interface LinhaCliente {
  key: string; posicao: number | null; nome: string; total: number; pct: number; pctAcumulado: number | null;
  cliente: FatCliente | null; demais: boolean;
}

export default function Faturamento12m({ podeVer = true, hoje: hojeProp }: PropsPagina) {
  const { pode, carregando: carregandoPerms, erro: erroPerms, recarregar } = usePerms();
  const liberado = podeVer && pode(PERM);
  const { hoje, filtro, setFiltro, janela } = usePeriodo(hojeProp, MAX_MESES_FATURAMENTO);

  const [agrupar, setAgrupar] = useState<Agrupar>('setores');
  const [grupo, setGrupo] = useState<TipoGrupoCliente>('cliente');
  const [visao, setVisao] = useState<'grafico' | 'tabela'>('grafico');
  const [setor, setSetor] = useState<string | null>(null);
  const [cliente, setCliente] = useState<{ chave: string; nome: string } | null>(null);
  /** null = automático: ligada com um setor escolhido, desligada com "Todos". */
  const [tendenciaManual, setTendenciaManual] = useState<boolean | null>(null);
  const [dados, setDados] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroDre | null>(null);
  const pedido = useRef(0);

  const de = janela.ok ? janela.de : null;
  const ate = janela.ok ? janela.ate : null;

  useEffect(() => {
    if (!liberado || !de || !ate) return;
    const meu = ++pedido.current;
    setCarregando(true); setErro(null);
    buscarFaturamento12m({ de, ate, agrupar, grupo })
      .then((d) => { if (meu === pedido.current) setDados(d); })
      .catch((e) => { if (meu === pedido.current) setErro(e instanceof ErroDre ? e : new ErroDre(String(e?.message || e), null)); })
      .finally(() => { if (meu === pedido.current) setCarregando(false); });
  }, [liberado, de, ate, agrupar, grupo]);

  const setores = useMemo(
    () => (dados ? setoresDaResposta(dados.setores, [...dados.meses.map((m) => m.por_setor), ...dados.por_setor.map((p) => ({ [p.setor]: p.valor }))]) : []),
    [dados],
  );
  // Setor que não existe no agrupamento atual (trocou 6 setores ↔ 5 frentes) vale "Todos".
  const setorEf = setor && setores.includes(setor) ? setor : null;
  const clienteObj = useMemo(
    () => (dados && cliente ? dados.clientes.find((c) => c.chave === cliente.chave) || null : null),
    [dados, cliente],
  );
  const semDivisao = !!clienteObj && !clienteObj.por_mes_setor;
  const serie = useMemo(() => {
    if (!dados) return [];
    if (cliente) return serieDoCliente(clienteObj || { por_mes: {}, por_mes_setor: {} }, dados.meses);
    return serieDaEmpresa(dados.meses);
  }, [dados, cliente, clienteObj]);
  const comTendencia = tendenciaManual ?? setorEf != null;
  const { linhas, tendencia } = useMemo(
    () => linhasDoGrafico(serie, setores, semDivisao ? null : setorEf, comTendencia),
    [serie, setores, semDivisao, setorEf, comTendencia],
  );
  const composicao = useMemo(() => composicaoPorSetor(serie, setores), [serie, setores]);
  const listaClientes = useMemo(
    () => (dados ? (setorEf ? clientesDoSetor(dados.clientes, setorEf) : dados.clientes) : []),
    [dados, setorEf],
  );
  const { top, demais } = useMemo(() => clientesTop(listaClientes, 15), [listaClientes]);
  const foraDaDre = useMemo(() => (dados ? dados.meses.filter((m) => Math.abs(m.fora_da_dre || 0) >= 0.01) : []), [dados]);
  const opcoesCliente = useMemo(() => {
    const lista = (dados?.clientes || []).slice().sort((a, b) => b.total - a.total).map((c) => ({ value: c.chave, label: c.nome }));
    if (cliente && !lista.some((o) => o.value === cliente.chave)) lista.unshift({ value: cliente.chave, label: cliente.nome });
    return [{ value: '__todos', label: dados?.grupo === 'cnpj_raiz' ? 'Todos os grupos' : 'Todos os clientes' }, ...lista];
  }, [dados, cliente]);

  if (carregandoPerms) return <Carregando />;
  if (!liberado) return podeVer && erroPerms ? <AcessoNaoConferido onTentar={recarregar} /> : <SemAcesso />;

  const escolherSetor = (s: string | null) => { setSetor(s); setTendenciaManual(null); };
  const escolherCliente = (chave: string) => {
    if (chave === '__todos') { setCliente(null); return; }
    const c = dados?.clientes.find((x) => x.chave === chave);
    setCliente({ chave, nome: c?.nome || opcoesCliente.find((o) => o.value === chave)?.label || chave });
  };
  const mesesFechados = linhas.filter((l) => !l.parcial).length;

  const controles = (
    <Space wrap size={[16, 8]} style={{ marginBottom: 12 }}>
      <FiltroDePeriodo filtro={filtro} onChange={setFiltro} hoje={hoje} janela={janela} />
      <FiltroDeSetor setores={setores} valor={setorEf} onChange={escolherSetor} />
      <Select
        aria-label="Cliente" value={cliente?.chave || '__todos'} onChange={escolherCliente} options={opcoesCliente}
        showSearch optionFilterProp="label" style={{ width: 260 }} popupMatchSelectWidth={false} disabled={!dados}
      />
      <FiltroDeTendencia
        ligado={comTendencia} onChange={setTendenciaManual}
        desligadoPorque={dados && mesesFechados < 2 ? 'Precisa de pelo menos 2 meses fechados no período.' : null}
      />
      <Segmented
        value={agrupar} onChange={(v) => setAgrupar(v as Agrupar)}
        options={[{ value: 'setores', label: '6 setores' }, { value: 'frentes', label: '5 frentes' }]}
      />
      <Segmented
        value={grupo} onChange={(v) => { setGrupo(v as TipoGrupoCliente); setCliente(null); }}
        options={[{ value: 'cliente', label: 'Cliente' }, { value: 'cnpj_raiz', label: 'Grupo (CNPJ raiz)' }]}
      />
    </Space>
  );
  const avisoJanela = janela.ok
    ? (janela.aviso ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message={janela.aviso} /> : null)
    : <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={janela.aviso} />;

  if (!janela.ok) return <div>{controles}{avisoJanela}</div>;
  if (erro && !dados) {
    return (
      <div>
        {controles}{avisoJanela}
        {erro.status === 403 ? <SemAcesso /> : <Alert type="error" showIcon message="Não foi possível carregar o faturamento" description={erro.message} />}
      </div>
    );
  }
  if (!dados) return <div>{controles}{avisoJanela}<Carregando texto="Somando as parcelas do período..." /></div>;

  const semSetor = dados.avisos?.sem_setor;
  const naoIdent = dados.avisos?.nao_identificados;
  const temParcial = dados.meses.some((m) => m.parcial);
  const ehGrupo = dados.grupo === 'cnpj_raiz';
  const rotuloSetor = setorEf ? metaSetor(setorEf).rotulo : null;
  const totalFoco = linhas.reduce((s, l) => s + l.total, 0);
  const totalCliente = serie.reduce((s, m) => s + m.total, 0);

  // Cabeçalho: um número só (o do recorte escolhido) e, em cinza, o peso dele no todo.
  const titulo = [cliente?.nome, rotuloSetor].filter(Boolean).join(' · ') || 'Faturamento';
  let peso: string | null = null;
  if (cliente && setorEf) peso = totalCliente ? `${pctTexto((totalFoco / totalCliente) * 100)} do faturamento deste ${ehGrupo ? 'grupo' : 'cliente'}` : null;
  else if (clienteObj) peso = `${clienteObj.posicao}º maior ${ehGrupo ? 'grupo' : 'cliente'} · ${pctTexto(clienteObj.pct)} do faturamento`;
  else if (cliente) peso = 'Sem faturamento neste período.';
  else if (setorEf) peso = dados.total ? `${pctTexto((totalFoco / dados.total) * 100)} do faturamento` : null;

  // Série de um número por mês (setor escolhido, ou cliente sem divisão por setor).
  const serieUnica = setorEf && !semDivisao
    ? serieDoSetor(serie, setorEf)
    : semDivisao ? linhas.map((l) => ({ periodo: l.periodo, parcial: l.parcial, valor: l.total, pct: null as number | null })) : null;
  const rotuloPctMes = cliente ? `% do ${ehGrupo ? 'grupo' : 'cliente'} no mês` : '% do mês';

  const barras = semDivisao ? ['total'] : setorEf ? [setorEf] : cliente ? setores.filter((s) => composicao.some((c) => c.setor === s)) : setores;
  const nSeries = barras.length + (tendencia ? 1 : 0);

  const colunasSerie = [
    { title: 'Mês', dataIndex: 'periodo', key: 'periodo', render: (p: string, r: any) => rotuloMesCurto(p) + (r.parcial ? '*' : '') },
    { title: 'Valor', dataIndex: 'valor', key: 'valor', align: 'right' as const, render: (v: number) => reais(v) },
    ...(setorEf && !semDivisao ? [{ title: rotuloPctMes, dataIndex: 'pct', key: 'pct', align: 'right' as const, render: (v: number | null) => pctTexto(v) }] : []),
  ];
  const colunasSetor = [
    { title: 'Setor', dataIndex: 'setor', key: 'setor', render: (s: string) => <EtiquetaSetor chave={s} /> },
    { title: 'Total', dataIndex: 'valor', key: 'valor', align: 'right' as const, render: (v: number) => reais(v) },
    { title: '%', dataIndex: 'pct', key: 'pct', align: 'right' as const, render: (v: number | null) => pctTexto(v) },
  ];

  // Visão "Tabela": setor × mês (com um setor escolhido, só a linha dele, sem repetir o total do cabeçalho).
  const setoresTabela = setorEf ? [setorEf] : composicao.map((c) => c.setor);
  const colunasMes = [
    { title: 'Setor', dataIndex: 'setor', key: 'setor', fixed: 'left' as const, render: (s: string) => <EtiquetaSetor chave={s} /> },
    ...serie.map((m) => ({
      title: rotuloMesCurto(m.periodo) + (m.parcial ? '*' : ''), key: m.periodo, align: 'right' as const,
      render: (_: any, r: any) => reais(r.meses[m.periodo] || 0),
    })),
    ...(setorEf ? [] : [
      { title: 'Total', key: 'total', align: 'right' as const, render: (_: any, r: any) => <Text strong>{reais(r.total)}</Text> },
      { title: '%', key: 'pct', align: 'right' as const, render: (_: any, r: any) => pctTexto(r.pct) },
    ]),
  ];
  const linhasMes = setoresTabela.map((s) => {
    const c = composicao.find((x) => x.setor === s);
    return { key: s, setor: s, total: c?.valor || 0, pct: c?.pct ?? null, meses: Object.fromEntries(serie.map((m) => [m.periodo, m.porSetor[s] || 0])) };
  });

  const linhasClientes: LinhaCliente[] = [
    ...top.map((c) => ({ key: c.chave, posicao: c.posicao, nome: c.nome, total: c.total, pct: c.pct, pctAcumulado: c.pct_acumulado, cliente: c, demais: false })),
    ...(demais ? [{ key: '__demais', posicao: null, nome: `Demais (${demais.n})`, total: demais.total, pct: demais.pct, pctAcumulado: null, cliente: null, demais: true }] : []),
  ];
  const colunasClientes = [
    { title: '#', key: 'pos', width: 48, render: (_: any, r: LinhaCliente) => r.posicao ?? '' },
    {
      title: ehGrupo ? 'Grupo' : 'Cliente', key: 'nome',
      render: (_: any, r: LinhaCliente) => (
        <span>
          {r.demais ? <Text type="secondary">{r.nome}</Text> : (
            r.cliente?.membros && r.cliente.membros.length > 1
              ? <TipAntd title={`Empresas do grupo: ${r.cliente.membros.map((x) => x.nome).join('; ')}`}>{r.nome}</TipAntd>
              : r.nome
          )}
          {r.cliente && !r.cliente.identificado ? (
            <TipAntd title="Não achamos este cliente no cadastro; o nome é o que veio escrito no lançamento.">
              <Tag color="orange" style={{ marginLeft: 6 }}>não identificado</Tag>
            </TipAntd>
          ) : null}
        </span>
      ),
    },
    { title: setorEf ? `Total em ${rotuloSetor}` : 'Total', key: 'total', align: 'right' as const, render: (_: any, r: LinhaCliente) => reais(r.total) },
    { title: '%', key: 'pct', align: 'right' as const, render: (_: any, r: LinhaCliente) => pctTexto(r.pct) },
    { title: '% acumulado', key: 'acum', align: 'right' as const, render: (_: any, r: LinhaCliente) => pctTexto(r.pctAcumulado) },
    ...(setorEf ? [] : [{
      title: 'Setores', key: 'setores',
      render: (_: any, r: LinhaCliente) => {
        if (!r.cliente) return null;
        const ps = Object.entries(porSetorCanonico(r.cliente.por_setor)).filter(([, v]) => Math.abs(v) >= 0.5).sort((a, b) => b[1] - a[1]);
        return (
          <Space size={[8, 2]} wrap>
            {ps.slice(0, 3).map(([s]) => <EtiquetaSetor key={s} chave={s} />)}
            {ps.length > 3 ? <Text type="secondary">+{ps.length - 3}</Text> : null}
          </Space>
        );
      },
    }]),
    {
      title: 'Contratos', key: 'contratos',
      render: (_: any, r: LinhaCliente) => {
        const lista = (r.cliente?.contratos || []).map(rotuloContrato).filter(Boolean);
        if (!lista.length) return null;
        return <TipAntd title={lista.join(', ')}><span>{lista.length}</span></TipAntd>;
      },
    },
    {
      title: '', key: 'ver', width: 110,
      render: (_: any, r: LinhaCliente) => (r.cliente ? (
        <Button size="small" type="link" icon={<BarChartOutlined />} onClick={() => { escolherCliente(r.cliente!.chave); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          ver no gráfico
        </Button>
      ) : null),
    },
  ];

  const contratosCliente = (clienteObj?.contratos || []).map(rotuloContrato).filter(Boolean);
  const vazio = !linhas.some((l) => Math.abs(l.total) >= 0.5);

  return (
    <div>
      {controles}
      {avisoJanela}

      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">{titulo}, de {rotuloMes(dados.de)} a {rotuloMes(dados.ate)}</Text>
        <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.2 }}>{reais(totalFoco)}</div>
        {peso ? <Text type="secondary">{peso}</Text> : null}
        {temParcial ? <div><Text type="secondary">* o último mês ainda está em andamento.</Text></div> : null}
        {cliente ? (
          <div style={{ marginTop: 4 }}>
            {clienteObj?.membros && clienteObj.membros.length > 1 ? <div><Text type="secondary">Empresas do grupo: </Text>{clienteObj.membros.map((x) => x.nome).join('; ')}</div> : null}
            {contratosCliente.length ? <div><Text type="secondary">Contratos: </Text>{contratosCliente.join(', ')}</div> : null}
            <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setCliente(null)}>Voltar para todos os {ehGrupo ? 'grupos' : 'clientes'}</Button>
          </div>
        ) : null}
      </div>

      <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
        {!cliente && semSetor && (Math.abs(semSetor.valor) >= 0.01 || semSetor.parcelas > 0 || semSetor.manuais > 0) ? (
          <Alert
            type="warning" showIcon
            message={`${reais(semSetor.valor)} sem setor (${semSetor.parcelas} parcela(s)${semSetor.manuais ? ` e ${semSetor.manuais} lançamento(s) manual(is)` : ''})`}
            description={<span>Para corrigir: <IrPara para={LINK_AREAS}>Operacional › Contratos › Áreas e CNAE</IrPara> (lista “Parcelas sem setor”).</span>}
          />
        ) : null}
        {!cliente && foraDaDre.length ? (
          <Alert
            type="info" showIcon
            message={`${reais(foraDaDre.reduce((s, m) => s + m.fora_da_dre, 0))} de receita antiga que a DRE não conta`}
            description={`Meses: ${foraDaDre.map((m) => `${rotuloMes(m.periodo)} (${reais(m.fora_da_dre)})`).join(', ')}. Estão no cadastro de recebimentos, mas sem setor, e por isso ficam fora do total.`}
          />
        ) : null}
        {naoIdent && naoIdent.n > 0 && !cliente ? (
          <Alert
            type="info" showIcon
            message={`${naoIdent.n} cliente(s) sem cadastro identificado (${reais(naoIdent.valor)})`}
            description="Aparecem com o nome escrito no lançamento. Se o nome estiver escrito de dois jeitos, o mesmo cliente pode aparecer em duas linhas."
          />
        ) : null}
        {semDivisao ? (
          <Alert type="info" showIcon message="O servidor ainda não manda a divisão por setor de cada cliente: o gráfico mostra só o total do mês." />
        ) : null}
        {erro ? <Alert type="error" showIcon message="Não consegui atualizar" description={erro.message} /> : null}
      </Space>

      <div style={{ opacity: carregando ? 0.5 : 1, transition: 'opacity .2s' }}>
        <Space style={{ marginBottom: 8 }} wrap>
          <Text strong>{rotuloSetor ? `${rotuloSetor}, mês a mês` : 'Por setor, mês a mês'}</Text>
          <Segmented size="small" value={visao} onChange={(v) => setVisao(v as 'grafico' | 'tabela')} options={[{ value: 'grafico', label: 'Gráfico' }, { value: 'tabela', label: 'Tabela' }]} />
        </Space>

        {vazio ? <Empty description={cliente ? 'Sem faturamento deste cliente no período' : 'Sem faturamento no período'} /> : visao === 'grafico' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ flex: '3 1 480px', minWidth: 0, height: 'clamp(280px, calc(100vh - 420px), 520px)' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={linhas} stackOffset="sign" margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={reaisCurto} width={56} />
                  <Tooltip content={(p: any) => (
                    <TooltipEmpilhado active={p.active} payload={p.payload} label={p.label} rotuloTotal="Total do mês" umaBarra={!semDivisao && setorEf != null} />
                  )} />
                  {nSeries >= 2 ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
                  {barras.map((s) => {
                    const m = s === 'total' ? { rotulo: ehGrupo ? 'Faturamento do grupo' : 'Faturamento do cliente', cor: COR_SO_TOTAL } : metaSetor(s);
                    return <Bar key={s} dataKey={s} name={m.rotulo} stackId="fat" fill={m.cor} stroke="#fff" strokeWidth={2} maxBarSize={48} />;
                  })}
                  {tendencia ? (
                    <Line
                      type="linear" dataKey="tendencia" name={`Tendência (${tendenciaTexto(tendencia.porMes)})`}
                      stroke={COR_TENDENCIA} strokeDasharray={TRACO_TENDENCIA} strokeWidth={2} dot={false} activeDot={false}
                      legendType="plainline" isAnimationActive={false}
                    />
                  ) : null}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: '2 1 300px', minWidth: 0 }}>
              {serieUnica ? (
                <Table size="small" pagination={false} columns={colunasSerie} dataSource={serieUnica.map((x) => ({ ...x, key: x.periodo }))} />
              ) : (
                <Table size="small" pagination={false} columns={colunasSetor} dataSource={composicao.map((p) => ({ ...p, key: p.setor }))} />
              )}
            </div>
          </div>
        ) : serieUnica && semDivisao ? (
          <Table size="small" pagination={false} columns={colunasSerie} dataSource={serieUnica.map((x) => ({ ...x, key: x.periodo }))} />
        ) : (
          <Table size="small" pagination={false} scroll={{ x: 'max-content' }} columns={colunasMes} dataSource={linhasMes} />
        )}
      </div>

      {!cliente ? (
        <div style={{ marginTop: 24, opacity: carregando ? 0.5 : 1, transition: 'opacity .2s' }}>
          <Text strong>Por {ehGrupo ? 'grupo de empresas' : 'cliente'}{rotuloSetor ? `, só em ${rotuloSetor}` : ''}</Text>
          <Text type="secondary"> (15 maiores; “ver no gráfico” mostra a evolução mês a mês e a divisão por setor)</Text>
          <Table
            style={{ marginTop: 8 }} size="small" pagination={false} scroll={{ x: 'max-content' }}
            columns={colunasClientes} dataSource={linhasClientes}
          />
        </div>
      ) : null}
    </div>
  );
}
